import React, { useEffect, useState, ChangeEvent } from "react";
import { FILE_DOWNLOAD_URL, FILES_FOLDER_LIST_URL } from "./constants";
import {
  deleteFile,
  downloadFile,
  fetchAllSftpData,
  uploadFile,
} from "./services/Sftpservice";
import FileActions from "./file-actions/FileActions";
import FileTable from "./main-table/FileTable";
import UploadDialog from "./file-actions/UploadDialog";
import FilePreviewDialog from "./file-actions/FilePreviewDialog";
import { File } from "./model/File";
import { axiosRequests } from "../../app/api/axiosRequest";
import getAxiosError from "../../app/api/axiosError";
import { toast } from "react-toastify";
import Loader from "../../components/common/loader/Loader";
import { Typography } from "@mui/material";

const FileManager: React.FC = () => {
  const [fileStructure, setFileStructure] = useState<File[]>([]);
  const [currentPath, setCurrentPath] = useState<string>("");
  const [isUploadOpen, setIsUploadOpen] = useState<boolean>(false);
  const [filePreview, setFilePreview] = useState<File | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  const getData = async (): Promise<void> => {
    const controller = new AbortController();
    const signal = controller.signal;
    const timeout = 1200000;

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);

    try {
      const data = await axiosRequests.getWithQueryParam<any>(
        FILES_FOLDER_LIST_URL + "?remoteDirectory=" + currentPath,
        { signal }
      );
      data.sort((d1: string | number | any , d2: string | number | any) => new Date(d2.date).getTime() -  new Date(d1.date).getTime());
      clearTimeout(timeoutId);
      const json: File[] = data;
      setFileStructure(json);
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, { toastId: `filefolderNotFound` });
    } finally {
      console.log("loading");
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [currentPath]);

  const openFolder = (name: string): void => {
    setLoading(true);
    setCurrentPath((prevPath) => `${prevPath}/${name}`);
  };

  const onDownload = async (name: string) => {
    const data = await downloadFile(currentPath, name);
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(data);
    downloadLink.download = name;
    document.body.appendChild(downloadLink);
    downloadLink.click();

    // Cleanup
    document.body.removeChild(downloadLink);
  };

  const goBack = (): void => {
    setLoading(true);
    setCurrentPath((prevPath) =>
      prevPath.substring(0, prevPath.lastIndexOf("/"))
    );
  };

  const handleViewFile = (file: File): void => {
    setFilePreview(file);
  };

  const handleDelete = (name: string): void => {
    setLoading(true);
    deleteFile(currentPath, name);
    setLoading(false);
    setFileStructure(fileStructure.filter((file) => file.name !== name));
  };

  const handleFileUpload = async (
    event: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const files = event.target.files;

    const formData = new FormData();
    if (files) {
      formData.append("file", files[0]);
    }

    try {
      const response = await uploadFile(formData, currentPath);
      const updatedFileStructure = await fetchAllSftpData(currentPath);
      setFileStructure(updatedFileStructure);
      setIsUploadOpen(false);
      event.target.value = "";
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, { toastId: `fileUploadError` });
    }
  };

  if (loading) return <Loader isLoading={loading} />;

  return (
    <>
      <FileActions
        onBack={goBack}
        onUploadClick={() => setIsUploadOpen(true)}
        showBackButton={currentPath.length > 0}
        disabled={!currentPath.toLowerCase().includes("out")} // Disable if not in 'inbound'
      />
      {currentPath !== "" && (
        <Typography style={{ padding: 2 }}>
          Current Directory: {currentPath}
        </Typography>
      )}
      {currentPath !== "" && (
        <Typography style={{ padding: 2 }}>
          Current Directory: {currentPath}
        </Typography>
      )}
      <FileTable
        files={fileStructure}
        onOpenFolder={openFolder}
        onViewFile={handleViewFile}
        onDelete={handleDelete}
        onDownload={onDownload}
      />
      <UploadDialog
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        onUpload={handleFileUpload}
      />

      <FilePreviewDialog
        file={filePreview}
        onClose={() => setFilePreview(null)}
      />
    </>
  );
};

export default FileManager;
